/**
 * Answer-style state for one app session: the device's guest preference and,
 * while someone is signed in, their account's — held in separate slots.
 *
 * - **Guest** state is read from and saved to device storage, with an in-memory
 *   fallback when storage is blocked (the UI then says the choice applies for
 *   this visit).
 * - **Account** state lives in memory only, hydrated from the server at sign-in
 *   and after each successful save, and dropped at sign-out. It is never written
 *   to device storage, so the next person on a shared device — signed in or not
 *   — never sees it.
 *
 * Kept byte-identical between the web and mobile clients; each platform supplies
 * only the storage adapter.
 */
import {
  ANSWER_STYLE_INVITE_STORAGE_KEY,
  ANSWER_STYLE_STORAGE_KEY,
  DEFAULT_ANSWER_STYLE,
  parseAnswerStyle,
  parseGuestAnswerStyle,
  parseInviteState,
  serializeGuestAnswerStyle,
  type AnswerStyle,
  type AnswerStyleInvite,
} from '@/components/chat/AnswerStyle';

export interface AnswerStyleStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
}

export interface AnswerStyleSnapshot {
  scope: 'guest' | 'account';
  /** Guest: device storage has been read. Account: the server value is applied. */
  hydrated: boolean;
  /** The style in force — the saved choice, or the default when none was made. */
  style: AnswerStyle;
  /** True only once the voter has saved a choice (a default is not a choice). */
  explicit: boolean;
  invite: AnswerStyleInvite | null;
  /** Where a guest save lands: `browser` storage, or `memory` when blocked. */
  guestStorage: 'browser' | 'memory';
}

/** Fields the caller should write to the account after sign-in (a one-time
 * migration of the guest's explicit choices onto an account that has none). */
export interface AnswerStyleMigration {
  answer_style?: AnswerStyle;
  answer_style_invite?: AnswerStyleInvite;
}

interface Slot {
  hydrated: boolean;
  style: AnswerStyle;
  explicit: boolean;
  invite: AnswerStyleInvite | null;
}

interface AccountSlot extends Slot {
  id: string;
  revision: number;
}

export function createAnswerStyleStore(storage: AnswerStyleStorage) {
  let guest: Slot = {
    hydrated: false,
    style: DEFAULT_ANSWER_STYLE,
    explicit: false,
    invite: null,
  };
  let guestStorage: 'browser' | 'memory' = 'browser';
  let account: AccountSlot | null = null;
  let loadPromise: Promise<void> | null = null;
  // Bumped whenever the guest slot changes locally, so a slow initial read
  // cannot overwrite a choice made while it was in flight.
  let guestRevision = 0;
  let accountQueue: Promise<unknown> = Promise.resolve();

  const listeners = new Set<() => void>();
  let snapshot: AnswerStyleSnapshot = derive();

  function derive(): AnswerStyleSnapshot {
    const slot = account ?? guest;
    return {
      scope: account ? 'account' : 'guest',
      hydrated: slot.hydrated,
      style: slot.style,
      explicit: slot.explicit,
      invite: slot.invite,
      guestStorage,
    };
  }

  function emit(): void {
    snapshot = derive();
    listeners.forEach((listener) => listener());
  }

  async function write(key: string, value: string): Promise<boolean> {
    try {
      await storage.setItem(key, value);
      return true;
    } catch {
      guestStorage = 'memory';
      return false;
    }
  }

  function load(): Promise<void> {
    if (loadPromise) return loadPromise;
    const startedAt = guestRevision;
    loadPromise = (async () => {
      let style: AnswerStyle | null = null;
      let invite: AnswerStyleInvite | null = null;
      try {
        style = parseGuestAnswerStyle(await storage.getItem(ANSWER_STYLE_STORAGE_KEY));
        invite = parseInviteState(await storage.getItem(ANSWER_STYLE_INVITE_STORAGE_KEY));
      } catch {
        guestStorage = 'memory';
      }
      if (guestRevision === startedAt) {
        guest = {
          hydrated: true,
          style: style ?? DEFAULT_ANSWER_STYLE,
          explicit: style !== null,
          invite,
        };
      } else {
        guest = { ...guest, hydrated: true };
      }
      emit();
    })();
    return loadPromise;
  }

  return {
    load,

    getSnapshot(): AnswerStyleSnapshot {
      return snapshot;
    },

    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    /** Save a guest's explicit choice. Resolves to where it was kept. */
    async saveGuest(style: AnswerStyle): Promise<'browser' | 'memory'> {
      guestRevision += 1;
      guest = { hydrated: true, style, explicit: true, invite: 'completed' };
      emit();
      const kept =
        (await write(ANSWER_STYLE_STORAGE_KEY, serializeGuestAnswerStyle(style))) &&
        (await write(ANSWER_STYLE_INVITE_STORAGE_KEY, 'completed'));
      emit();
      return kept ? 'browser' : 'memory';
    },

    /** Retire the guest invitation (skipped, or bypassed by starting a chat). */
    async dismissGuestInvite(): Promise<void> {
      if (guest.invite) return;
      guestRevision += 1;
      guest = { ...guest, hydrated: true, invite: 'dismissed' };
      emit();
      await write(ANSWER_STYLE_INVITE_STORAGE_KEY, 'dismissed');
      emit();
    },

    /** The account revision to capture before reading the server, and pass to
     * `applyAccount` — a save committed in between wins over the older read. */
    accountRevision(): number {
      return account?.revision ?? 0;
    },

    /**
     * Apply the signed-in account's server values (from `/auth/callback` or
     * `GET /user/settings`) and switch to the account slot.
     *
     * Precedence, per account:
     * 1. An explicitly saved account style wins over anything on the device.
     * 2. With none, the guest's explicit choice (never a default, never a skip)
     *    carries over — returned as `answer_style` for the caller to save.
     * 3. The invite state follows the same order, so a voter who already
     *    answered the invitation as a guest is not asked again.
     */
    async applyAccount(
      id: string,
      server: { answer_style?: unknown; answer_style_invite?: unknown },
      revisionAtRead?: number
    ): Promise<AnswerStyleMigration> {
      await load();
      const serverStyle = parseAnswerStyle(server.answer_style);
      const serverInvite = parseInviteState(server.answer_style_invite);
      const sameAccount = account?.id === id;

      const readIsStale =
        sameAccount && revisionAtRead !== undefined && account?.revision !== revisionAtRead;
      if (readIsStale) {
        // A save landed after this read began; the read is older than it.
        return {};
      }

      const migration: AnswerStyleMigration = {};
      let style = DEFAULT_ANSWER_STYLE;
      let explicit = false;
      if (serverStyle) {
        style = serverStyle;
        explicit = true;
      } else if (sameAccount && account?.explicit) {
        style = account.style;
        explicit = true;
      } else if (!sameAccount && guest.explicit) {
        style = guest.style;
        explicit = true;
        migration.answer_style = guest.style;
      }

      // A saved style already completed the invitation; a guest skip carries
      // over only onto an account that has answered it neither way.
      let invite = serverInvite ?? (serverStyle ? 'completed' : null);
      if (!invite && sameAccount && account?.invite) invite = account.invite;
      if (!invite && !sameAccount && guest.invite) {
        invite = guest.invite;
        if (!migration.answer_style) migration.answer_style_invite = guest.invite;
      }
      if (!invite && migration.answer_style) invite = 'completed';

      account = {
        id,
        hydrated: true,
        style,
        explicit,
        invite,
        revision: (sameAccount && account ? account.revision : 0) + 1,
      };
      emit();
      return migration;
    },

    /**
     * Save an explicit account choice. Saves run one at a time in the order
     * they were made, and the store changes only after the server confirms, so
     * a failure leaves the previous saved value in force (and on screen).
     */
    saveAccount(id: string, style: AnswerStyle, persist: () => Promise<void>): Promise<void> {
      const run = accountQueue.then(async () => {
        await persist();
        if (account?.id !== id) return;
        account = {
          ...account,
          hydrated: true,
          style,
          explicit: true,
          invite: 'completed',
          revision: account.revision + 1,
        };
        emit();
      });
      accountQueue = run.catch(() => undefined);
      return run;
    },

    /** Retire the account's invitation locally; the caller writes it best-effort. */
    dismissAccountInvite(id: string): void {
      if (!account || account.id !== id || account.invite) return;
      account = { ...account, invite: 'dismissed', revision: account.revision + 1 };
      emit();
    },

    /** Sign-out or account switch: drop every account-derived value. */
    clearAccount(): void {
      if (!account) return;
      account = null;
      accountQueue = Promise.resolve();
      emit();
    },
  };
}

export type AnswerStyleStore = ReturnType<typeof createAnswerStyleStore>;
